import {MetaEventType} from "./MetaEventType";
import {MetaAdsService} from "../../../../../services/ads/meta/MetaAdsService";
import {IMetaEventOutgoing} from "../../../../../services/ads/meta/IMetaEventOutgoing";
import {config} from "../../../../../config";

declare global {
    interface Window {
        fbq: any;
        _fbq: any;
    }
}

const META_PIXEL_ID = process.env.META_PIXEL_ID;
const BATCH_FLUSH_INTERVAL = 20000;
const BATCH_MAX_SIZE = 50;

export class SedestralProactiveMeta {
    private static installed = false;
    private static consentGranted = false;
    private static pixelLoaded = false;

    private static batchQueue: IMetaEventOutgoing[] = [];
    private static batchSiteId: string | undefined;
    private static batchFlushTimer: ReturnType<typeof setInterval> | null = null;

    private static getCookie(name: string): string | null {
        const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
        return match ? match[2] : null;
    }

    private static getFbc(): string | null {
        const fbcCookie = this.getCookie('_fbc');
        if (fbcCookie) return fbcCookie;

        const urlParams = new URLSearchParams(window.location.search);
        const fbclid = urlParams.get('fbclid');
        if (fbclid) {
            const timestamp = Math.floor(Date.now() / 1000);
            return `fb.1.${timestamp}.${fbclid}`;
        }

        return null;
    }

    private static getFbp(): string | null {
        return this.getCookie('_fbp');
    }

    static async init(): Promise<void> {
        if (config.isWhiteLabel) return;
        if (!META_PIXEL_ID || META_PIXEL_ID === '') {
            console.debug('Meta Pixel ID not configured, skipping initialization');
            return;
        }

        try {
            this.initPixel();
            this.installed = true;
            this.grantConsent();
            this.initBatch();
        } catch (err) {
            console.error('Failed to initialize Meta Pixel:', err);
        }
    }

    private static initBatch(): void {
        this.batchFlushTimer = setInterval(() => {
            this.flushBatch();
        }, BATCH_FLUSH_INTERVAL);

        window.addEventListener('beforeunload', () => {
            this.flushBatchBeacon();
        });
    }

    static trackBatch(
        eventType: MetaEventType,
        params?: {
            value?: number;
            currency?: string;
            contentName?: string;
            contentCategory?: string;
            contentIds?: string;
            searchString?: string;
        },
        siteId?: string
    ): void {
        if (!this.isEnabled()) return;

        const eventId = this.generateEventId();
        const fbc = this.getFbc();
        const fbp = this.getFbp();

        this.batchSiteId = siteId;
        this.batchQueue.push({
            eventType,
            eventId,
            sourceUrl: window.location.href,
            fbc: fbc || undefined,
            fbp: fbp || undefined,
            value: params?.value,
            currency: params?.currency,
            contentName: params?.contentName,
            contentCategory: params?.contentCategory,
            contentIds: params?.contentIds,
            searchString: params?.searchString
        });

        if (this.batchQueue.length >= BATCH_MAX_SIZE) {
            this.flushBatch();
        }
    }

    private static async flushBatch(): Promise<void> {
        if (this.batchQueue.length === 0) return;

        const events = this.batchQueue.splice(0, BATCH_MAX_SIZE);
        try {
            await MetaAdsService.sendEventBatch(events, this.batchSiteId);
        } catch (err) {
            console.error('Failed to flush Meta CAPI batch:', err);
        }
    }

    private static flushBatchBeacon(): void {
        if (this.batchQueue.length === 0) return;

        const events = this.batchQueue.splice(0);
        MetaAdsService.sendEventBatchBeacon(events, this.batchSiteId);
    }

    private static initPixel(): void {
        if (window.fbq) return;

        const n: any = window.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };

        if (!window._fbq) window._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];

        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://connect.facebook.net/en_US/fbevents.js';
        script.onload = () => {
            SedestralProactiveMeta.pixelLoaded = true;
        };

        const firstScript = document.getElementsByTagName('script')[0];
        if (firstScript && firstScript.parentNode) {
            firstScript.parentNode.insertBefore(script, firstScript);
        }

        window.fbq('init', META_PIXEL_ID);
        window.fbq('set', 'autoConfig', false, META_PIXEL_ID);
    }

    static grantConsent(): void {
        this.consentGranted = true;
    }

    static revokeConsent(): void {
        this.consentGranted = false;
    }

    static isEnabled(): boolean {
        return this.installed && this.consentGranted;
    }

    static disable(): void {
        this.consentGranted = false;
        if (window.fbq) {
            window.fbq('consent', 'revoke');
        }
    }

    static enable(): void {
        if (!this.installed) return;
        this.consentGranted = true;
        if (window.fbq) {
            window.fbq('consent', 'grant');
        }
    }

    private static generateEventId(): string {
        return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    }

    private static trackPixelEvent(eventName: string, eventId: string, params?: Record<string, any>): void {
        if (!this.isEnabled()) return;

        try {
            window.fbq('track', eventName, params || {}, {eventID: eventId});
        } catch (err) {
            console.error('Failed to track Meta Pixel event:', err);
        }
    }

    private static async sendCapiEvent(
        eventType: MetaEventType,
        eventId: string,
        params?: {
            value?: number;
            currency?: string;
            contentName?: string;
            contentCategory?: string;
            contentIds?: string;
            searchString?: string;
        },
        siteId?: string
    ): Promise<void> {
        const fbc = this.getFbc();
        const fbp = this.getFbp();

        try {
            await MetaAdsService.sendEvent({
                eventType,
                eventId,
                sourceUrl: window.location.href,
                fbc: fbc || undefined,
                fbp: fbp || undefined,
                value: params?.value,
                currency: params?.currency,
                contentName: params?.contentName,
                contentCategory: params?.contentCategory,
                contentIds: params?.contentIds,
                searchString: params?.searchString
            }, siteId);
        } catch (err) {
            console.error('Failed to send Meta CAPI event:', err);
        }
    }

    static async track(
        eventType: MetaEventType,
        params?: {
            value?: number;
            currency?: string;
            contentName?: string;
            contentCategory?: string;
            contentIds?: string;
            searchString?: string;
        },
        siteId?: string
    ): Promise<void> {
        if (!this.isEnabled()) return;

        const eventId = this.generateEventId();
        const eventNameMap: Record<MetaEventType, string> = {
            [MetaEventType.PAGE_VIEW]: 'PageView',
            [MetaEventType.COMPLETE_REGISTRATION]: 'CompleteRegistration',
            [MetaEventType.PURCHASE]: 'Purchase',
            [MetaEventType.LEAD]: 'Lead',
            [MetaEventType.INITIATE_CHECKOUT]: 'InitiateCheckout',
            [MetaEventType.ADD_TO_CART]: 'AddToCart',
            [MetaEventType.VIEW_CONTENT]: 'ViewContent',
            [MetaEventType.SEARCH]: 'Search',
            [MetaEventType.ADD_PAYMENT_INFO]: 'AddPaymentInfo',
            [MetaEventType.ADD_TO_WISHLIST]: 'AddToWishlist',
            [MetaEventType.CONTACT]: 'Contact',
            [MetaEventType.SUBSCRIBE]: 'Subscribe',
            [MetaEventType.START_TRIAL]: 'StartTrial'
        };

        const pixelParams: Record<string, any> = {};
        if (params?.value !== undefined) pixelParams.value = params.value;
        if (params?.currency) pixelParams.currency = params.currency;
        if (params?.contentName) pixelParams.content_name = params.contentName;
        if (params?.contentCategory) pixelParams.content_category = params.contentCategory;
        if (params?.contentIds) pixelParams.content_ids = params.contentIds;
        if (params?.searchString) pixelParams.search_string = params.searchString;

        this.trackPixelEvent(eventNameMap[eventType], eventId, pixelParams);

        if (eventType === MetaEventType.PAGE_VIEW) {
            this.trackBatch(eventType, params, siteId);
            return;
        }

        await this.sendCapiEvent(eventType, eventId, params, siteId);
    }

    static async trackPageView(siteId?: string): Promise<void> {
        try {
            await this.track(MetaEventType.PAGE_VIEW, undefined, siteId);
        } catch (e) {
            console.error(e);
        }
    }

    static async trackPurchase(amount: number, currency: string = 'EUR', siteId?: string): Promise<void> {
        await this.track(MetaEventType.PURCHASE, {
            value: amount,
            currency
        }, siteId);
    }

    static async trackInitiateCheckout(value?: number, currency: string = 'EUR', siteId?: string): Promise<void> {
        await this.track(MetaEventType.INITIATE_CHECKOUT, {
            value,
            currency
        }, siteId);
    }
}
